import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { App } from "./App";
import "./index.css";

// Sentry is opt-in: skip init entirely when VITE_SENTRY_DSN is unset so dev
// runs and forks don't pollute the production project. Set the DSN as a
// Cloudflare Pages env var (see DEPLOYMENT.md "Sentry").
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const apiUrl = import.meta.env.VITE_API_URL as string | undefined;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    // The Worker is careful not to capture client IPs outside `audit_log`.
    // Don't undo that posture by sending PII automatically to Sentry.
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // 100% trace sampling is fine — 2 known users, free tier covers it.
    tracesSampleRate: 1.0,
    // Distributed tracing only propagates to the Worker origin we control.
    tracePropagationTargets: apiUrl ? [apiUrl] : [],
    // No proactive session replay — only capture replay around errors.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    environment: import.meta.env.MODE,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("missing #root element");

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p className="p-4">Something went wrong.</p>}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
